/* eslint-disable import/first */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputLabel } from '@wajeez-web/components/ui';
import { TinyMCEFormType } from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// import tinymce from 'tinymce/tinymce.min.js';

// TinyMCE wants to be in global scope, even if loaded from npm module

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// window.tinymce = tinymce;

import { Editor } from '@tinymce/tinymce-react';
import {
  convertHtmlToMarkdown,
  convertMarkdownToHtml,
  convertHtmlToMarkdownPoem,
  convertMarkdownToHtmlPoem,
} from '@wajeez-web/core/utils';
import { ContentEntityType } from '@wajeez-web/stores/content';

export interface FormTinyMCEInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: TinyMCEFormType;
  disabled?: boolean;
  isRtl?: boolean;
  contentEntityType?: string;
}

const FormTinyMCEInput = <TFormFields extends FieldValues>({
  fieldType,
  disabled,
  isRtl = true,
  contentEntityType,
  ...props
}: FormTinyMCEInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,
      minLength:
        fieldType.fieldOptions.minLength != undefined
          ? fieldType.fieldOptions.minLength
          : undefined,
      maxLength:
        fieldType.fieldOptions.maxLength != undefined
          ? fieldType.fieldOptions.maxLength
          : undefined,
      ...props.rules,
    },
  });
  // const [html, setHtml] = useState(convertMarkdownToHtml(field.value || ''));

  const { t } = useTranslation();

  // useEffect(() => {
  //   const markdown = convertHtmlToMarkdown(html);
  //   if (field.value != markdown) {
  //     field.onChange(markdown);
  //     field.onBlur();
  //     // fieldChanged();
  //   }
  //   // return () => {};
  // }, [html]);

  // function fieldChanged() {
  //   const inHtml = convertMarkdownToHtml(field.value || '');
  //   console.log({ inHtml });
  //   if (inHtml != html) {
  //     setHtml(inHtml);
  //   }
  // }

  // useEffect(() => {
  //   console.log('field.value', field.value);
  //   fieldChanged();
  // }, [field.value]);

  // TODO: This should be used
  const isError = fieldState.error;

  const markdownToHtmlConverter = (markdown: string) => {
    switch (contentEntityType) {
      case ContentEntityType.POEM:
        return convertMarkdownToHtmlPoem(markdown);
      default:
        return convertMarkdownToHtml(markdown);
    }
  };

  const htmlToMarkdownConverter = (html: string) => {
    switch (contentEntityType) {
      case ContentEntityType.POEM:
        return convertHtmlToMarkdownPoem(html);
      default:
        return convertHtmlToMarkdown(html);
    }
  };

  const renderLabelDisclaimer = () => {
    switch (contentEntityType) {
      case ContentEntityType.POEM:
        return ` (${t(`inputs.disclaimers.poem text`)})`;
      default:
        return '';
    }
  };

  const initialValue = useRef(
    markdownToHtmlConverter(field.value || '')
  ).current;

  useLayoutEffect(() => {
    const style = document.createElement('style');

    style.innerHTML = `
      .tox .tox-edit-area::before {
        border: 2px solid ${!isError ? 'var(--theme-primary)' : '#EF4444'};
      }
    `;

    document.body.prepend(style);
  }, [isError]);

  return (
    <div>
      {fieldType.label && (
        <InputLabel
          text={`${
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          } ${renderLabelDisclaimer()}`}
          name={field.name}
          required={fieldType.required}
        />
      )}
      <Editor
        licenseKey="gpl"
        tinymceScriptSrc={'assets/scripts/tinymce/tinymce.min.js'}
        initialValue={initialValue}
        disabled={disabled || false}
        init={{
          ClassName: `${isError ? 'border-red-500' : ''}`,
          placeholder: t(
            `inputs.placeholders.${fieldType.placeHolder}`,
            fieldType.placeHolder || ''
          ) as string,
          directionality: isRtl ? 'rtl' : 'ltr',
          height: fieldType.fieldOptions.height || 400,
          menubar: false,
          contextmenu: 'paste link',
          // invalid_elements: 'table,td,tr,th,span',
          // only markdown compatible elements
          valid_elements:
            'p,br,b,strong,i,em,h1,h2,h3,h4,h5,h6,ul,ol,li,a[href],img[src|alt|width|height],pre,code,blockquote',
          text_patterns: [
            { start: '*', end: '*', format: 'italic' },
            { start: '**', end: '**', format: 'bold' },
            { start: '#', format: 'h1' },
            { start: '##', format: 'h2' },
            { start: '###', format: 'h3' },
            { start: '####', format: 'h4' },
            { start: '#####', format: 'h5' },
            { start: '######', format: 'h6' },
            // The following text patterns require the `lists` plugin
            { start: '1. ', cmd: 'InsertOrderedList' },
            { start: '* ', cmd: 'InsertUnorderedList' },
            { start: '- ', cmd: 'InsertUnorderedList' },
            { start: '~', end: '~', cmd: 'createLink' },
          ],
          // advlist_bullet_styles: 'default', // only include square bullets in list
          plugins: [
            // 'textpattern',
            // 'paste',
            'lists',
            'fullscreen',
            'searchreplace',
            // 'advlist', // doesn't support arabic by default
            'autolink',
            'link',
            'visualblocks',
            'code',
            'emoticons',
            'insertdatetime',
            'media',
            'table',
            'help',
            'wordcount',
            'directionality',
          ],
          toolbar:
            'undo redo | formatselect | bold italic | numlist bullist | link emoticons | removeformat',
          automatic_uploads: true,
        }}
        onEditorChange={(s) => {
          const middlePoints = s; // extractSpanFromHtml(html);

          // middlePoints = extractSpanFromHtml(middlePoints);
          // middlePoints = replaceHtmlCommentTags(middlePoints);
          // middlePoints = removeHtmlComments(middlePoints);
          // middlePoints = moveSpacesFromStrongAndEmTags(middlePoints);

          field.onChange(htmlToMarkdownConverter(middlePoints));
          field.onBlur();

          // setHtml(middlePoints);
        }}
      />
      {isError && (
        <p className="text-sm text-red-600">
          {fieldState.error?.type === 'minLength'
            ? t('inputs.messages.min1Length')
            : t('inputs.messages.Required')}
        </p>
      )}
    </div>
  );
};

export default FormTinyMCEInput;
